import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { DisabledReasonTooltip } from '@/components/DisabledReasonTooltip/DisabledReasonTooltip'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import type {
  ClusterBinding,
  LicenseResponseBody,
} from '@/features/Licenses/api/licenseHooks'
import { RegenerateConnectionStringForm } from '@/features/Licenses/components/RegenerateConnectionStringForm'
import { useCanManageLicenses } from '@/features/Licenses/hooks/useCanManageLicenses'
import {
  Badge,
  Box,
  Button,
  DataList,
  Dialog,
  HStack,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const AUDIT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'UTC',
})

/**
 * A license bound to a large fleet would otherwise stretch this card far past
 * the two beside it, so only the first few bindings show until an operator asks
 * for the rest.
 */
const BINDINGS_PREVIEW_LIMIT = 2

function bindingStatusColor(status: ClusterBinding['status']): string {
  if (status === 'BOUND') {
    return 'green'
  }
  return 'gray'
}

type Props = {
  license: LicenseResponseBody
}

/**
 * Which clusters this license is currently bound to — the runtime counterpart
 * to the entitlements above ("what does it grant?" → "where is it in use?").
 *
 * The panel owns exactly one operator action: rotating a binding's Data
 * Intelligence connection string. It lives here, per row, rather than in the
 * page header because each binding carries its own agent and its own
 * credential (APP-2548) — a license-level control would misrepresent the data
 * model and rotate something that does not exist.
 *
 * The `admin` gate lives here too, rather than on the page: this is the only
 * admin-only affordance in the license surface, and gtm can otherwise reach
 * everything on it. The panel therefore owns its own `Dialog`, unlike
 * `MetadataPanel` / `EntitlementsPanel`, which hand an action id up via
 * `onAction` for `LicenseActionDialog` to render.
 */
export function ClusterBindingsPanel({ license }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const { isAdmin } = useCanManageLicenses()
  const [rotatingBinding, setRotatingBinding] = useState<ClusterBinding | null>(
    null,
  )
  /* Reported up by the form. The panel owns the `Dialog.Root`, so only the
     panel can refuse escape and backdrop — and it has to, because the form
     withholds its inline statuses from the toast. */
  const [isRotating, setIsRotating] = useState(false)
  const closeDialog = () => {
    setRotatingBinding(null)
    setIsRotating(false)
  }
  const dash = String(t('licenses.detail.metadata.emptyDash'))
  const utcSuffix = String(t('licenses.detail.metadata.auditUtcSuffix'))

  const formatLastSeen = (epochMs: number | undefined) => {
    if (epochMs == null) {
      return dash
    }
    return `${AUDIT_DATE_FORMAT.format(new Date(epochMs))} ${utcSuffix}`
  }

  const bindings = license.clusterBindings ?? []
  const [showAllBindings, setShowAllBindings] = useState(false)

  const hiddenCount = Math.max(bindings.length - BINDINGS_PREVIEW_LIMIT, 0)
  const visibleBindings = showAllBindings
    ? bindings
    : bindings.slice(0, BINDINGS_PREVIEW_LIMIT)

  return (
    <>
      <SectionCard
        h="100%"
        title={String(t('licenses.detail.clusterBindings.title'))}
      >
        {/* A missing array and an empty one render identically. The distinction
          is "backend predates APP-2260" vs "genuinely unbound", which an
          operator cannot act on either way — and collapsing them keeps the
          panel from flashing a different state during the backend rollout. */}
        {bindings.length === 0 ? (
          <Text fontSize="sm" color="text.text-subtle">
            {String(t('licenses.detail.clusterBindings.empty'))}
          </Text>
        ) : (
          <VStack align="stretch" gap={4}>
            <VStack
              as="ul"
              align="stretch"
              gap={4}
              listStyleType="none"
              aria-label={String(
                t('licenses.detail.clusterBindings.listLabel'),
              )}
            >
              {visibleBindings.map((binding, index) => (
                <Box
                  as="li"
                  key={binding.clusterId}
                  // Bindings stack vertically rather than sitting in columns: this
                  // card is one of three side by side, so a row of three fields
                  // would crush a UUID-length cluster id.
                  pt={index === 0 ? 0 : 4}
                  borderTopWidth={index === 0 ? 0 : '1px'}
                  borderColor="border.muted"
                >
                  {/* Same `DataList` + `CopyableValue` pairing as the other cards
                    on this page, so a cluster id here copies exactly like the
                    license id next door. */}
                  <DataList.Root orientation="vertical" gap={2}>
                    <DataList.Item>
                      <DataList.ItemLabel>
                        {String(t('licenses.detail.clusterBindings.clusterId'))}
                      </DataList.ItemLabel>
                      <DataList.ItemValue>
                        {/* Status rides alongside the id rather than claiming a
                          labelled row of its own: it qualifies *this* cluster,
                          and pairing them halves the height of each binding. */}
                        <HStack gap={2} minW={0}>
                          <CopyableValue
                            value={binding.clusterId}
                            fontFamily="mono"
                          />
                          <Badge
                            colorPalette={bindingStatusColor(binding.status)}
                            variant="subtle"
                            flexShrink={0}
                          >
                            {binding.status}
                          </Badge>
                        </HStack>
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.ItemLabel>
                        {String(
                          t('licenses.detail.clusterBindings.lastSeenAt'),
                        )}
                      </DataList.ItemLabel>
                      <DataList.ItemValue>
                        <CopyableValue
                          value={formatLastSeen(binding.lastSeenAt)}
                          hasCopy={false}
                        />
                      </DataList.ItemValue>
                    </DataList.Item>
                  </DataList.Root>
                  {/* Trailing element rather than a third column beside the
                    cluster id: this card is one of three side by side, and the
                    row already gives its full width to a UUID. */}
                  {isAdmin ? (
                    <Box mt={2}>
                      <DisabledReasonTooltip
                        reason={
                          binding.dataIntelligenceAgentId
                            ? undefined
                            : String(
                                t(
                                  'licenses.actions.disabledReason.bindingHasNoAgentYet',
                                ),
                              )
                        }
                      >
                        <Button
                          size="xs"
                          variant="outline"
                          colorPalette="red"
                          alignSelf="start"
                          /* `null` and an absent field are the same normal state
                           — not-minted-yet — so one falsy check covers both. */
                          disabled={!binding.dataIntelligenceAgentId}
                          /* The accessible name names the cluster: it overrides
                           the visible "Regenerate", which is what makes two
                           rows tellable apart by name. */
                          aria-label={String(
                            t(
                              'licenses.actions.regenerateConnectionString.rowAriaLabel',
                              { clusterId: binding.clusterId },
                            ),
                          )}
                          onClick={() => setRotatingBinding(binding)}
                        >
                          {String(
                            t(
                              'licenses.actions.regenerateConnectionString.rowLabel',
                            ),
                          )}
                        </Button>
                      </DisabledReasonTooltip>
                    </Box>
                  ) : null}
                </Box>
              ))}
            </VStack>

            {hiddenCount > 0 ? (
              <Button
                variant="subtle"
                size="xs"
                alignSelf="start"
                onClick={() => setShowAllBindings((shown) => !shown)}
              >
                {showAllBindings
                  ? String(t('licenses.detail.clusterBindings.showFewer'))
                  : String(
                      t('licenses.detail.clusterBindings.showMore', {
                        count: hiddenCount,
                      }),
                    )}
              </Button>
            ) : null}
          </VStack>
        )}
      </SectionCard>
      {/* One dialog for the panel, not one per row: `rotatingBinding` is both
          the open flag and the target, so the two can never disagree. */}
      <Dialog.Root
        open={rotatingBinding !== null}
        /* A rotation in flight owns the dialog. Its failure modes are inline
           only — `500 CONNECTION_STRING_NOT_PERSISTED` above all, which leaves
           the credential dead until the operator retries from this very
           dialog — so dismissing mid-request would produce no feedback at all.
           Declining here covers escape and backdrop; the two props say the
           same thing to Chakra so the machine never starts closing. */
        closeOnEscape={!isRotating}
        closeOnInteractOutside={!isRotating}
        onOpenChange={(details) => {
          if (!details.open && !isRotating) {
            closeDialog()
          }
        }}
        size="md"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              {rotatingBinding ? (
                <RegenerateConnectionStringForm
                  license={license}
                  binding={rotatingBinding}
                  onSuccess={closeDialog}
                  onPendingChange={setIsRotating}
                />
              ) : null}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}
